import React, { useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { SelectButton } from 'primereact/selectbutton';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { EventAttendees, EventReminders, EventTime } from './components';
import { displayActionCardHeader } from 'modules/automation/utils/displayActionCardHeader';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import useGetEventTypes from 'modules/file_manager/hooks/useGetEventTypes';
import useLocationsList from 'hooks/useLocationsList';
import { dueDateDropdownList, eventConfigOptions } from 'constants/automation';
import { AttachmentInput, FormLabel, TrackTime } from 'modules/automation/WorkflowBuilder/components';
import { BY_EVENT_TYPE, BY_PRACTICE_AREA } from 'constants/automation';

const CreateEvent = ({ index }) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext();
  const trigger = watch('trigger');
  const selectedOptions = watch('filters')?.map((f) => f?.label);

  const practiceAreaId = useMemo(() => {
    const filters = watch('filters');
    return filters?.filter((f) => f?.label === BY_PRACTICE_AREA)?.map((f) => f?.value?.['case_description.case_practice_area'] || []);
  }, [JSON.stringify(watch('filters'))]);

  const eventWithScheduleDate = watch(`actions[${index}].event_with_schedule_date`);
  const assigneeReminder = useWatch({ name: `actions[${index}].assignee_reminder`, control }) || [];
  const isEventTypeSelected = useMemo(() => selectedOptions?.includes(BY_EVENT_TYPE), [selectedOptions]);

  const [toggleLocation, setToggleLocation] = useState(false);
  const { locationsList, isLocationLoading, handleAddLocation } = useLocationsList();
  const { typeList, isEventTypeLoading } = useGetEventTypes(isEventTypeSelected && practiceAreaId);

  const isReminderRequired = useMemo(() => {
    return assigneeReminder.some((reminder) => reminder?.send_reminder);
  }, [assigneeReminder]);

  const eventTypeCustomTemplate = (item, props) => {
    if (item) {
      const { event_type_icon = '', event_type = '' } = item || {};
      return (
        <div className="d-flex align-items-center">
          {event_type_icon ? (
            <span className="mx-2">
              <i className={event_type_icon} />
            </span>
          ) : null}{' '}
          {event_type}
        </div>
      );
    }

    return <span>{props?.placeholder ? props?.placeholder : null}</span>;
  };

  return (
    <>
      {displayActionCardHeader('Create New Event')}

      {trigger?.code === 'TRIG_EVENT_CREATE' && (
        <div className="row g-3 mb-2">
          <div className="col-12 col-lg-6">
            <FormLabel label="Create this event based on trigger event scheduled date" />
            <Controller
              name={`actions[${index}].event_with_schedule_date`}
              defaultValue={false}
              control={control}
              render={({ field: { value, name, onChange } }) => (
                <InputSwitch checked={Boolean(value)} name={name} onChange={(e) => onChange(e.value)} className="input-swich" />
              )}
            />
          </div>
          {eventWithScheduleDate && (
            <div className="col-12 col-lg-6">
              <FormLabel label="Create this event before/after the trigger event" isRequired />
              <Controller
                name={`actions[${index}].event_preference`}
                control={control}
                defaultValue="event_before_schedule_date"
                rules={{ required: 'Required' }}
                render={({ field: { value, onChange } }) => (
                  <SelectButton
                    optionLabel="name"
                    optionValue="value"
                    value={value}
                    options={eventConfigOptions}
                    onChange={(e) => onChange(e.value)}
                  ></SelectButton>
                )}
              />
              <RequiredValidation errors={errors} field={`actions.${index}.event_preference`} />
            </div>
          )}
        </div>
      )}
      <div className="row g-3">
        <div className="col-12 col-lg-6">
          {/* Event Title */}
          <div className="col-12 mb-3">
            <FormLabel label="Event Title" isRequired />
            <Controller
              name={`actions[${index}].title`}
              control={control}
              rules={{ required: 'Required' }}
              render={({ field }) => <TextSnippetWrapper {...field} placeholder="Event Title" className="input-shadow w-100" />}
            />
            <RequiredValidation errors={errors} field={`actions.${index}.title`} />
          </div>
          {/* Event Date Count & Unit */}
          <div className="col-12 mb-3">
            <FormLabel label="Event Date and Time" isRequired />
            <div className="row">
              <div className="col-6 pe-2">
                <Controller
                  name={`actions[${index}].meeting_cycle`}
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InputText {...field} keyfilter="int" min={0} className="input-shadow input-height w-100" placeholder="Type Count" />
                  )}
                />
                <RequiredValidation errors={errors} field={`actions.${index}.meeting_cycle`} message="Required" />
              </div>
              <div className="col-6 ps-2">
                <Controller
                  name={`actions[${index}].meeting_due_date_period`}
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field: { value, onChange, ref, onBlur } }) => (
                    <Dropdown
                      inputRef={ref}
                      value={value}
                      onChange={(e) => onChange(e.value)}
                      onBlur={onBlur}
                      options={dueDateDropdownList}
                      placeholder="Select"
                      className="input-height w-100"
                    />
                  )}
                />
                <RequiredValidation errors={errors} field={`actions.${index}.meeting_due_date_period`} message="Required" />
              </div>
            </div>
            <EventTime index={index} />
          </div>
          {/* Attendees - as one column */}
          <div className="col-12 mb-3">
            <FormLabel label="Attendees" />
            <EventAttendees index={index} />
          </div>
          {/* Description */}
          <div className="col-12 mb-3">
            <FormLabel label="Description" />
            <Controller
              name={`actions[${index}].description`}
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  style={{ minHeight: 148 }}
                  {...field}
                  type="textarea"
                  placeholder="Description"
                  className="input-shadow w-100"
                  rows={6}
                />
              )}
            />
          </div>
          {/* Reminders - one cell (col-12 col-lg-6) */}
          <div className="col-12 mb-3">
            <EventReminders index={index} optionText="before event" isRequired={isReminderRequired} />
          </div>
        </div>

        <div className="col-12 col-lg-6">
          {/* Video Conferencing URL */}
          <div className="col-12 mb-3">
            <FormLabel label="Video Conferencing URL" />
            <Controller
              name={`actions[${index}].meeting_url`}
              placeholder="http://example.com or https://example.com"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper {...field} type="text" className="input-shadow w-100 p-2" placeholder="Meeting URL" />
              )}
            />
          </div>

          {/* Event Type */}
          <div className="col-12 mb-3">
            <FormLabel label="Event Type" />
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Controller
                name={`actions[${index}].event_type`}
                control={control}
                render={({ field: { value, ref, onChange, onBlur } }) => (
                  <Dropdown
                    inputRef={ref}
                    value={value?.event_type}
                    onChange={(e) => onChange({ event_type: e.value })}
                    onBlur={onBlur}
                    options={typeList || []}
                    isFilter
                    optionLabel="event_type"
                    optionValue="event_type_id"
                    itemTemplate={eventTypeCustomTemplate}
                    valueTemplate={eventTypeCustomTemplate}
                    placeholder="Event Type"
                    className="flex-grow-1 w-100"
                    dropdownIcon={isEventTypeLoading ? 'pi pi-spin pi-spinner' : undefined}
                  />
                )}
              />
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  let path = '/settings/custom-descriptors';
                  localStorage?.setItem('stateData', JSON.stringify({ activeIndex: 3, currentGroupLabel: 2 }));
                  window?.open(window?.location?.protocol + '//' + window?.location?.host + '/#' + path, '_blank');
                }}
                className="pt-1 text-decoration-none ms-2 mb-3"
              >
                Customize Event Type
              </a>
            </div>
          </div>

          {/* Location */}
          <div className="col-12 mb-3">
            <FormLabel label="Location" />
            <div className="d-flex g-2 align-items-center">
              <div className="d-flex w-100">
                {toggleLocation ? (
                  <div className="p-inputgroup">
                    <Controller
                      name={`actions[${index}].location_name`}
                      control={control}
                      render={({ field }) => <TextSnippetWrapper {...field} className="input-shadow" placeholder="Location Name" />}
                    />
                    <Button
                      icon="pi pi-check"
                      className="p-button-secondary outline mx-2"
                      type="button"
                      onClick={() => {
                        const title = getValues(`actions[${index}].location_name`);
                        handleAddLocation(title, setToggleLocation, setValue, `actions[${index}].location`);
                      }}
                    />
                    <Button
                      icon="pi pi-times"
                      className="p-button-secondary outline"
                      type="button"
                      onClick={() => setToggleLocation(false)}
                    />
                  </div>
                ) : (
                  <Controller
                    name={`actions[${index}].location`}
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        options={locationsList || []}
                        filter
                        className="w-100"
                        placeholder="Select Location"
                        dropdownIcon={isLocationLoading ? 'pi pi-spin pi-spinner' : undefined}
                      />
                    )}
                  />
                )}
              </div>
              <div className="d-flex align-items-center">
                <span className="pointer">
                  <i className="fas fa-plus-circle ms-2" onClick={() => setToggleLocation(true)}></i>
                </span>
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="col-12 mb-3">
            <FormLabel label="Attachments" />
            <AttachmentInput index={index} />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateEvent;
